import { useCallback, useEffect, useRef, useState } from "react";
import debounce from "lodash.debounce";

export function useSpeechSend(
  sendMessage: (msg: string) => void,
  onInterim: (text: string) => void,
  stopPlayingAudio: () => void
) {
  const recognitionRef = useRef<SpeechRecognition | null>(null);
  const [listening, setListening] = useState(false);
  const transcriptRef = useRef<string>("");
  const lastFinalTextRef = useRef("");

  // debounce sending after 2.5s of “silence”
  const debouncedSend = useRef(
    debounce(() => {
      const text = transcriptRef.current.trim();
      if (!text) return;

      stopPlayingAudio();
      sendMessage(text);
      transcriptRef.current = "";
      setListening(false);
    }, 2500)
  ).current;

  const stopListening = useCallback(() => {
    if (!listening || !recognitionRef.current) return;
    recognitionRef.current.stop();
    debouncedSend.cancel(); // clear any pending send
    setListening(false);
  }, [listening, debouncedSend]);

  const initRecognizer = useCallback(() => {
    const SpeechRecognition =
      (window as any).SpeechRecognition ||
      (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition) {
      console.warn("Web Speech API not supported");
      return null;
    }

    const rec = new SpeechRecognition();
    rec.continuous = true;
    rec.interimResults = true;
    rec.lang = "en-US"; // adjust as needed

    rec.onresult = (evt: SpeechRecognitionEvent) => {
      let newFinalText = "";
      let interimText = "";

      for (let i = evt.resultIndex; i < evt.results.length; i++) {
        const result = evt.results[i];
        const transcriptPiece = result[0].transcript;

        if (result.isFinal) {
          newFinalText += transcriptPiece;
        } else {
          interimText += transcriptPiece;
        }
      }

      if (newFinalText) {
        // Add only truly new final text
        transcriptRef.current += newFinalText.slice(
          lastFinalTextRef.current.length
        );
        lastFinalTextRef.current = transcriptRef.current;
      }

      const combined = (
        transcriptRef.current +
        " " +
        interimText.slice(lastFinalTextRef.current.length)
      ).trim();

      onInterim(combined);
      debouncedSend();
    };

    rec.onend = () => {
      if (listening) {
        // on many mobiles, continuous stops after a while
        rec.start();
      }
    };

    rec.onerror = (e: unknown) => {
      console.error("Speech recognition error", e);
      stopListening();
    };

    return rec;
  }, [onInterim, debouncedSend, listening, stopListening]);

  // start/stop controls
  const startListening = useCallback(() => {
    if (listening) return;
    if (!recognitionRef.current) {
      recognitionRef.current = initRecognizer();
      if (!recognitionRef.current) return;
    }
    transcriptRef.current = "";
    lastFinalTextRef.current = "";
    recognitionRef.current.start();
    setListening(true);
  }, [listening, initRecognizer]);

  useEffect(() => {
    const requestMicrophoneAccess = async () => {
      try {
        await navigator.mediaDevices.getUserMedia({ audio: true });
      } catch (error) {
        alert("Please enable microphone access in your browser settings.");
      }
    };

    const checkMicPermission = async () => {
      try {
        const permissionStatus = await navigator.permissions.query({
          name: "microphone" as PermissionName,
        });

        return permissionStatus.state;
      } catch (error) {
        console.error("Permissions API not supported:", error);
        return "prompt";
      }
    };

    requestMicrophoneAccess();
    checkMicPermission();
  }, []);

  // clean up on unmount
  useEffect(() => {
    return () => {
      stopListening();
      debouncedSend.cancel();
    };
  }, [stopListening, debouncedSend]);

  useEffect(() => {
    const handleVisibilityChange = () => {
      if (document.visibilityState === "hidden") {
        stopListening();
      }
    };

    document.addEventListener("visibilitychange", handleVisibilityChange);
    return () => {
      document.removeEventListener("visibilitychange", handleVisibilityChange);
    };
  }, [stopListening]);

  return { startListening, stopListening, listening };
}
